import { createContext, useContext, useSyncExternalStore } from 'react';
import {
  UNAVAILABLE_UNREAD_TEXT_COUNT_STORE,
  type UnreadTextCountStore,
} from './unreadTextCountStore';

export const UnreadTextCountStoreContext =
  createContext<UnreadTextCountStore | null>(null);

/**
 * Current team-wide unread text-conversation count, or `undefined` when it is
 * unavailable (before the first fetch resolves, or when the registry never
 * answers). Never coerced to `0`.
 */
export const useUnreadTextCount = (): number | undefined => {
  const store =
    useContext(UnreadTextCountStoreContext) ??
    UNAVAILABLE_UNREAD_TEXT_COUNT_STORE;

  return useSyncExternalStore(store.subscribe, store.getCount);
};

/**
 * Write access to the count. Throws without a provider: unlike the read path, a
 * silently discarded write would leave the badge permanently empty with no
 * signal at all, which is the failure mode this feature is most likely to hit.
 */
export const useUnreadTextCountSetter =
  (): UnreadTextCountStore['setCount'] => {
    const store = useContext(UnreadTextCountStoreContext);

    if (!store) {
      throw new Error(
        'useUnreadTextCountSetter must be used inside UnreadTextCountProvider',
      );
    }

    return store.setCount;
  };
